// src/services/auth.service.ts
import { myFetch, HttpMethod } from './baseService';

// Tipos basados en tu estructura de React Native
export interface AuthUserRecord {
  usu_codigo: string;
  usu_email: string;
  usu_nombre_completo: string;
  usu_contrasena: string;
  usu_numero_celular: string;
  usu_master: number;
  ure_codigo: string;
  ure_nombre: string;
  tus_codigo?: string;
  tus_nombre?: string;
  apt_codigo?: string;
  apt_nombre?: string;
  uau_permiso_para_votar?: number;
}

export interface ApiEnvelope<T> {
  code: string | number;
  message: string;
  token_?: string;
  object_: T;
}

export interface UnidadResidencial {
  id: string;
  nombre: string;
}

export interface RolUsuario {
  id: string;
  nombre: string;
}

export interface Apartamento {
  id: string;
  nombre: string;
}

// Requests para cada paso
export interface Auth1Request {
  email: string;
  contrasena: string;
}

export interface Auth2Request extends Auth1Request {
  CodigoUnidadResidencial: string;
}

export interface Auth3Request extends Auth2Request {
  TipoUsuario: string;
}

export interface Auth4Request extends Auth3Request {
  CodigoApartamento: string;
}

// Clase personalizada para errores de API
export class ApiError extends Error {
  public code: string | number;
  public serverMessage: string;

  constructor(code: string | number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.serverMessage = message;
  }
}

// Función para validar respuesta del servidor
function validateApiResponse<T>(envelope: ApiEnvelope<T>): void {
  // Códigos de éxito conocidos del sistema
  const successCodes = ["0", "1", "11", 0, 1, 11];
  
  // Códigos de error conocidos
  const errorCodes = ["-1", -1];
  
  // Si es un código de error conocido, lanzar error
  if (errorCodes.includes(envelope.code)) {
    throw new ApiError(envelope.code, envelope.message || 'Error del servidor');
  }
  
  // Si no es un código de éxito conocido, también considerar como posible error
  // pero ser más permisivo con códigos numéricos positivos
  if (!successCodes.includes(envelope.code)) {
    // Si es un número positivo mayor a 0, probablemente sea éxito
    const numericCode = typeof envelope.code === 'string' ? parseInt(envelope.code) : envelope.code;
    if (typeof numericCode === 'number' && numericCode > 0) {
      // Permitir códigos positivos como éxito
      console.log(`Código de respuesta no reconocido pero positivo: ${envelope.code}. Considerando como éxito.`);
      return;
    }
    
    // Si no es positivo, lanzar error
    throw new ApiError(envelope.code, envelope.message || 'Error del servidor');
  }
}

// Funciones de mapeo (igual que en React Native)
export function mapUnidades(list: AuthUserRecord[]): UnidadResidencial[] {
  return Array.from(
    new Map(
      list.map((u) => [u.ure_codigo, { id: u.ure_codigo, nombre: u.ure_nombre }])
    ).values()
  );
}

export function mapRoles(list: AuthUserRecord[]): RolUsuario[] {
  return Array.from(
    new Map(
      list
        .filter((u) => !!u.tus_codigo && !!u.tus_nombre)
        .map((u) => [u.tus_codigo as string, { id: u.tus_codigo as string, nombre: u.tus_nombre as string }])
    ).values()
  );
}

export function mapApartamentos(list: AuthUserRecord[]): Apartamento[] {
  return Array.from(
    new Map(
      list
        .filter((u) => !!u.apt_codigo)
        .map((u) => [u.apt_codigo!, { id: u.apt_codigo!, nombre: u.apt_nombre ?? '' }])
    ).values()
  );
}

// Servicios de autenticación
export const loginAndGetUnidades = async (payload: Auth1Request) => {
  try {
    const envelope = await myFetch<ApiEnvelope<AuthUserRecord[]>>(
      'usuarioAutenticacion1/Autenticacion_1',
      HttpMethod.POST,
      payload
    );

    // Validar respuesta del servidor
    validateApiResponse(envelope);

    if (!Array.isArray(envelope.object_)) {
      throw new Error('Respuesta inesperada del servidor');
    }

    const unidades = mapUnidades(envelope.object_);
    return { envelope, unidades };
  } catch (error) {
    // Si es un error de API, lo relanzamos tal como está
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Si es otro tipo de error, lo envolvemos
    throw new Error(error instanceof Error ? error.message : 'Error de conexión');
  }
};

export const validarUnidadYGetRoles = async (payload: Auth2Request) => {
  try {
    const envelope = await myFetch<ApiEnvelope<AuthUserRecord[]>>(
      'usuario/autenticacion_2',
      HttpMethod.POST,
      payload
    );

    validateApiResponse(envelope);

    if (!Array.isArray(envelope.object_)) {
      throw new Error('Respuesta inesperada del servidor');
    }

    const roles = mapRoles(envelope.object_);
    return { envelope, roles };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new Error(error instanceof Error ? error.message : 'Error de conexión');
  }
};

export const validarRolYGetApartamentos = async (payload: Auth3Request) => {
  try {
    const envelope = await myFetch<ApiEnvelope<AuthUserRecord[]>>(
      'usuario/autenticacion_3',
      HttpMethod.POST,
      payload
    );

    validateApiResponse(envelope);

    if (!Array.isArray(envelope.object_)) {
      throw new Error('Respuesta inesperada del servidor');
    }

    const apartamentos = mapApartamentos(envelope.object_);
    return { envelope, apartamentos };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new Error(error instanceof Error ? error.message : 'Error de conexión');
  }
};

export const confirmarAccesoFinal = async (payload: Auth4Request) => {
  try {
    const envelope = await myFetch<ApiEnvelope<AuthUserRecord[]>>(
      'usuario/autenticacion_4',
      HttpMethod.POST,
      payload
    );

    validateApiResponse(envelope);

    return envelope;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new Error(error instanceof Error ? error.message : 'Error de conexión');
  }
};